import { useState } from "react";
import { Copy, Download, Pencil, Plus, Save, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const periodicidades = [
  { value: "mensal", label: "Mensal" },
  { value: "trimestral", label: "Trimestral" },
  { value: "semestral", label: "Semestral" },
  { value: "anual", label: "Anual" },
] as const;

const mesesControleEmissao = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

type FaixaBonificadaForm = {
  quantidadePacotes: string;
  metragemBonificadaM2: string;
};

type ConfiguracaoForm = {
  emitenteId: string;
  dataInicio: string;
  periodicidadeReajuste: "mensal" | "trimestral" | "semestral" | "anual";
  valorM2Armazenado: string;
  valorPacoteExpedido: string;
  valorPacoteEnvioFull: string;
  prazoDescontoMeses: string;
  slaAtrasoPercentual: string;
  slaFalhaPercentual: string;
  valorUnidadeRetrabalhada: string;
};

type ConfiguracaoB2bArmazenagemForm = {
  emitenteId: string;
  valorPorPedido: string;
  valorPorSku: string;
  valorPorQuantidade: string;
  quantidadeBonificadaPorSku: string;
  valorM3Armazenado: string;
  valorPacoteEnvioFull: string;
  prazoDescontoMeses: string;
  slaAtrasoPercentual: string;
  slaFalhaPercentual: string;
  valorUnidadeRetrabalhada: string;
};

type SaldoArmazenagem = {
  sku: string;
  quantidade: number;
};

const formularioVazio: ConfiguracaoForm = {
  emitenteId: "",
  dataInicio: "",
  periodicidadeReajuste: "anual",
  valorM2Armazenado: "",
  valorPacoteExpedido: "",
  valorPacoteEnvioFull: "",
  prazoDescontoMeses: "",
  slaAtrasoPercentual: "",
  slaFalhaPercentual: "",
  valorUnidadeRetrabalhada: "",
};

const formularioB2bArmazenagemVazio: ConfiguracaoB2bArmazenagemForm = {
  emitenteId: "",
  valorPorPedido: "",
  valorPorSku: "",
  valorPorQuantidade: "",
  quantidadeBonificadaPorSku: "",
  valorM3Armazenado: "",
  valorPacoteEnvioFull: "",
  prazoDescontoMeses: "",
  slaAtrasoPercentual: "",
  slaFalhaPercentual: "",
  valorUnidadeRetrabalhada: "",
};

function nomeEmitente(emitente: any) {
  return emitente?.razaoSocial || emitente?.nomeFantasia || `Emitente ${emitente?.id ?? ""}`;
}

function valorTela(valor: unknown, casas = 2) {
  return Number(valor ?? 0).toLocaleString("pt-BR", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

export default function FechamentosBox() {
  const { user } = useGoogleAuth();
  const utils = trpc.useUtils();
  const [abaAtiva, setAbaAtiva] = useState<"fechamento" | "controleEmissao" | "configuracoes">("fechamento");
  const [tipoFechamento, setTipoFechamento] = useState<"b2c_armazenagem" | "b2b_armazenagem">("b2c_armazenagem");
  const [emitenteRelatorioId, setEmitenteRelatorioId] = useState("");
  const [competencia, setCompetencia] = useState("");
  const [anoControleEmissao, setAnoControleEmissao] = useState("2026");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formulario, setFormulario] = useState<ConfiguracaoForm>(formularioVazio);
  const [faixasBonificadas, setFaixasBonificadas] = useState<FaixaBonificadaForm[]>([]);
  const [configuracaoParaDuplicar, setConfiguracaoParaDuplicar] = useState<any | null>(null);
  const [emitenteDestinoId, setEmitenteDestinoId] = useState("");
  const [saldosArmazenagem, setSaldosArmazenagem] = useState<SaldoArmazenagem[]>([]);
  const [nomeArquivoArmazenagem, setNomeArquivoArmazenagem] = useState("");
  const [arquivoSaldoBase64, setArquivoSaldoBase64] = useState("");
  const [emitentePrincipalUnificacaoId, setEmitentePrincipalUnificacaoId] = useState("");
  const [relatoriosUnificacaoIds, setRelatoriosUnificacaoIds] = useState<number[]>([]);
  const [b2bEditingId, setB2bEditingId] = useState<number | null>(null);
  const [formularioB2bArmazenagem, setFormularioB2bArmazenagem] = useState<ConfiguracaoB2bArmazenagemForm>(formularioB2bArmazenagemVazio);

  const isAdmin = user?.role === "admin";
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery(undefined, { enabled: isAdmin });
  const emitentesAtendidos = emitentes.filter((emitente: any) => !emitente.operadorLogistico);
  const { data: configuracoes = [], isLoading: carregandoConfiguracoes } = trpc.fechamentosBox.listConfiguracoes.useQuery(undefined, { enabled: isAdmin });
  const { data: configuracoesB2bArmazenagem = [], isLoading: carregandoConfiguracoesB2bArmazenagem } = trpc.fechamentosBox.listB2bArmazenagemConfiguracoes.useQuery(undefined, { enabled: isAdmin });
  const { data: relatorios = [], isLoading: carregandoRelatorios } = trpc.fechamentosBox.listRelatorios.useQuery(undefined, { enabled: isAdmin });
  const { data: relatoriosB2bArmazenagem = [], isLoading: carregandoRelatoriosB2bArmazenagem } = trpc.fechamentosBox.listB2bArmazenagemRelatorios.useQuery(undefined, { enabled: isAdmin });
  const { data: unificacoes = [], isLoading: carregandoUnificacoes } = trpc.fechamentosBox.listUnificacoes.useQuery(undefined, { enabled: isAdmin });
  const relatoriosFiltrados = relatorios.filter((relatorio: any) =>
    (!emitenteRelatorioId || Number(relatorio.emitenteId) === Number(emitenteRelatorioId))
    && (!competencia || relatorio.competencia === competencia)
  );
  const relatoriosB2bArmazenagemFiltrados = relatoriosB2bArmazenagem.filter((relatorio: any) =>
    (!emitenteRelatorioId || Number(relatorio.emitenteId) === Number(emitenteRelatorioId))
    && (!competencia || relatorio.competencia === competencia)
  );
  const relatoriosUnificaveis = (() => {
    const porEmitente = new Map<number, any>();
    for (const relatorio of relatorios.filter((item: any) => item.competencia === competencia)) {
      if (!porEmitente.has(Number(relatorio.emitenteId))) porEmitente.set(Number(relatorio.emitenteId), relatorio);
    }
    return Array.from(porEmitente.values());
  })();
  const anosControleEmissao = Array.from(new Set([
    2026,
    new Date().getFullYear(),
    ...relatorios.map((relatorio: any) => Number(String(relatorio.competencia).slice(0, 4))).filter(Number.isFinite),
    ...relatoriosB2bArmazenagem.map((relatorio: any) => Number(String(relatorio.competencia).slice(0, 4))).filter(Number.isFinite),
  ])).sort((a, b) => b - a);
  const competenciasEmitidas = new Set([
    ...relatorios.map((relatorio: any) => `${Number(relatorio.emitenteId)}-${relatorio.competencia}`),
    ...relatoriosB2bArmazenagem.map((relatorio: any) => `${Number(relatorio.emitenteId)}-${relatorio.competencia}`),
  ]);
  const anoControleSelecionado = Number(anoControleEmissao);
  const competenciaNaoAplicavel = (mes: number) => anoControleSelecionado === 2026 && mes < 8;
  const competenciaAtualControle = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;
  const faltaEmitir = emitentesAtendidos.flatMap((emitente: any) => mesesControleEmissao.flatMap((mesNome, indice) => {
    const mes = indice + 1;
    const competenciaControle = `${anoControleEmissao}-${String(mes).padStart(2, "0")}`;
    if (competenciaControle >= competenciaAtualControle || competenciaNaoAplicavel(mes) || competenciasEmitidas.has(`${Number(emitente.id)}-${competenciaControle}`)) return [];
    return [{ emitenteNome: nomeEmitente(emitente), competencia: competenciaControle, mesNome }];
  }));

  const salvarConfiguracao = trpc.fechamentosBox.salvarConfiguracao.useMutation({
    onSuccess: () => {
      toast.success("Configuração de fechamento salva.");
      utils.fechamentosBox.listConfiguracoes.invalidate();
      limparFormulario();
    },
    onError: (erro) => toast.error(erro.message),
  });

  const salvarConfiguracaoB2bArmazenagem = trpc.fechamentosBox.salvarB2bArmazenagemConfiguracao.useMutation({
    onSuccess: () => {
      toast.success("Configuração B2B + Armazenagem salva.");
      utils.fechamentosBox.listB2bArmazenagemConfiguracoes.invalidate();
      limparFormularioB2bArmazenagem();
    },
    onError: (erro) => toast.error(erro.message),
  });

  const duplicarConfiguracao = trpc.fechamentosBox.duplicarConfiguracao.useMutation({
    onSuccess: () => {
      toast.success("Configuração duplicada.");
      utils.fechamentosBox.listConfiguracoes.invalidate();
      setConfiguracaoParaDuplicar(null);
      setEmitenteDestinoId("");
    },
    onError: (erro) => toast.error(erro.message),
  });

  const prepararB2cArmazenagem = trpc.fechamentosBox.prepararB2cArmazenagem.useMutation({
    onSuccess: async (fechamento) => {
      try {
        const XLSX = await import("xlsx");
        const resumo = [{
          "Tipo de relatório": "B2C + Armazenamento",
          "Emitente": fechamento.emitente.emitenteNome,
          "Competência": fechamento.competencia,
          "Valor por pacote expedido (R$)": fechamento.configuracao.valorPacoteExpedido,
          "Valor de armazenagem cadastrado (R$)": fechamento.configuracao.valorM2Armazenado,
          "Pedidos": fechamento.resumo.totalPedidos,
          "Custo pedidos (R$)": fechamento.resumo.totalPedidosExpedidos,
          "Solicitações Resolve": fechamento.resumo.totalSolicitacoesResolve,
          "Impacto Resolve (R$)": fechamento.resumo.totalImpactoResolve,
          "Total de endereços": fechamento.resumo.totalEnderecos,
          "Produtos com armazenagem não cobrada": fechamento.resumo.totalProdutosArmazenagemNaoCobrada,
          "Custo armazenagem (R$)": fechamento.resumo.totalArmazenagem,
          "TOTAL GERAL (R$)": fechamento.resumo.totalGeral,
        }];
        const pedidos = fechamento.pedidos.map((pedido: any) => ({
          "Código BIP": pedido.codigoBip,
          "Data": pedido.dataEntrada,
          "Marketplace": pedido.marketplaceNome,
          "Tipo de envio": pedido.tipoEnvioNome,
          "Status atual": pedido.statusAtual,
          "Valor por pedido (R$)": pedido.valorPacoteExpedido,
          "Custo de pedido (R$)": pedido.custoExpedicao,
        }));
        const resolve = fechamento.resolve.map((solicitacao: any) => ({
          "Ticket": solicitacao.ticket,
          "Tema": solicitacao.tema,
          "Tipo": solicitacao.tipoNome,
          "Status": solicitacao.status,
          "Data de custo": solicitacao.dataCusto,
          "Impacto no fechamento (R$)": solicitacao.impactoNoFechamento,
        }));
        const armazenagem = fechamento.armazenagem.map((item: any) => ({
          "SKU": item.skuPlanilha,
          "Quantidade": item.quantidade,
          "Descrição": item.descricao,
          "Metragem total (m³)": item.metragemTotalM3,
        }));

        const criarAba = (dados: any[], larguras: number[]) => {
          const aba = XLSX.utils.json_to_sheet(dados);
          aba["!cols"] = larguras.map((wch) => ({ wch }));
          return aba;
        };
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, criarAba(resumo, [26, 45, 14, 31, 29, 18, 28, 33, 24, 27, 22]), "Resumo");
        XLSX.utils.book_append_sheet(workbook, criarAba(pedidos, [24, 16, 28, 28, 16, 24, 26]), "Pedidos");
        XLSX.utils.book_append_sheet(workbook, criarAba(resolve, [18, 48, 26, 30, 20, 25]), "Resolve");
        XLSX.utils.book_append_sheet(workbook, criarAba(armazenagem, [20, 14, 48, 24]), "Armazenagem");
        XLSX.writeFile(workbook, `fechamento_b2c_armazenagem_${fechamento.emitente.id}_${fechamento.competencia}.xlsx`);
        toast.success(`Planilha gerada${fechamento.resumo.totalProdutosArmazenagemNaoCobrada ? ` com ${fechamento.resumo.totalProdutosArmazenagemNaoCobrada} produto(s) com armazenagem não cobrada` : ""}.`);
      } catch {
        toast.error("Erro ao gerar a planilha de fechamento.");
      }
    },
    onError: (erro) => toast.error(erro.message),
  });

  const baixarArquivoRelatorio = trpc.fechamentosBox.baixarArquivoRelatorio.useMutation({
    onSuccess: (arquivo) => {
      const link = document.createElement("a");
      link.href = `data:application/octet-stream;base64,${arquivo.arquivoBase64}`;
      link.download = arquivo.nomeArquivo;
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    onError: (erro) => toast.error(erro.message),
  });

  const baixarArquivoUnificado = trpc.fechamentosBox.baixarArquivoUnificado.useMutation({
    onSuccess: (arquivo) => {
      const link = document.createElement("a");
      link.href = `data:application/octet-stream;base64,${arquivo.arquivoBase64}`;
      link.download = arquivo.nomeArquivo;
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    onError: (erro) => toast.error(erro.message),
  });

  const gerarB2cArmazenagem = trpc.fechamentosBox.gerarB2cArmazenagem.useMutation({
    onSuccess: (fechamento) => {
      utils.fechamentosBox.listRelatorios.invalidate();
      baixarArquivoRelatorio.mutate({ id: fechamento.id, tipoArquivo: "relatorio" });
      toast.success(`Fechamento V${fechamento.versao} salvo${fechamento.resumo.totalProdutosArmazenagemNaoCobrada ? ` com ${fechamento.resumo.totalProdutosArmazenagemNaoCobrada} produto(s) com armazenagem não cobrada` : ""}.`);
    },
    onError: (erro) => toast.error(erro.message),
  });

  const baixarArquivoB2bArmazenagemRelatorio = trpc.fechamentosBox.baixarArquivoB2bArmazenagemRelatorio.useMutation({
    onSuccess: (arquivo) => {
      const link = document.createElement("a");
      link.href = `data:application/octet-stream;base64,${arquivo.arquivoBase64}`;
      link.download = arquivo.nomeArquivo;
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    onError: (erro) => toast.error(erro.message),
  });

  const gerarB2bArmazenagem = trpc.fechamentosBox.gerarB2bArmazenagem.useMutation({
    onSuccess: (fechamento) => {
      utils.fechamentosBox.listB2bArmazenagemRelatorios.invalidate();
      baixarArquivoB2bArmazenagemRelatorio.mutate({ id: fechamento.id });
      toast.success(`Fechamento B2B V${fechamento.versao} salvo.`);
    },
    onError: (erro) => toast.error(erro.message),
  });

  const gerarUnificado = trpc.fechamentosBox.gerarUnificado.useMutation({
    onSuccess: (fechamento) => {
      utils.fechamentosBox.listRelatorios.invalidate();
      utils.fechamentosBox.listUnificacoes.invalidate();
      setEmitentePrincipalUnificacaoId("");
      setRelatoriosUnificacaoIds([]);
      baixarArquivoUnificado.mutate({ id: fechamento.id, tipoArquivo: "relatorio" });
      toast.success("Fechamento unificado salvo.");
    },
    onError: (erro) => toast.error(erro.message),
  });

  function limparFormularioB2bArmazenagem() {
    setB2bEditingId(null);
    setFormularioB2bArmazenagem(formularioB2bArmazenagemVazio);
  }

  function atualizarFormularioB2bArmazenagem(campo: keyof ConfiguracaoB2bArmazenagemForm, valor: string) {
    setFormularioB2bArmazenagem((atual) => ({ ...atual, [campo]: valor }));
  }

  function editarConfiguracaoB2bArmazenagem(configuracao: any) {
    setAbaAtiva("configuracoes");
    setB2bEditingId(configuracao.id);
    setFormularioB2bArmazenagem({
      emitenteId: String(configuracao.emitenteId),
      valorPorPedido: String(configuracao.valorPorPedido ?? ""),
      valorPorSku: String(configuracao.valorPorSku ?? ""),
      valorPorQuantidade: String(configuracao.valorPorQuantidade ?? ""),
      quantidadeBonificadaPorSku: String(configuracao.quantidadeBonificadaPorSku ?? ""),
      valorM3Armazenado: String(configuracao.valorM3Armazenado ?? ""),
      valorPacoteEnvioFull: String(configuracao.valorPacoteEnvioFull ?? ""),
      prazoDescontoMeses: String(configuracao.prazoDescontoMeses ?? ""),
      slaAtrasoPercentual: String(configuracao.slaAtrasoPercentual ?? ""),
      slaFalhaPercentual: String(configuracao.slaFalhaPercentual ?? ""),
      valorUnidadeRetrabalhada: String(configuracao.valorUnidadeRetrabalhada ?? ""),
    });
  }

  function gerarFechamentoB2bArmazenagem() {
    if (!emitenteRelatorioId || !competencia) {
      toast.error("Selecione o emitente e a competência.");
      return;
    }
    gerarB2bArmazenagem.mutate({
      emitenteId: Number(emitenteRelatorioId),
      competencia,
    });
  }

  function salvarB2bArmazenagem(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    const numeros = [
      formularioB2bArmazenagem.valorPorPedido,
      formularioB2bArmazenagem.valorPorSku,
      formularioB2bArmazenagem.valorPorQuantidade,
      formularioB2bArmazenagem.quantidadeBonificadaPorSku,
      formularioB2bArmazenagem.valorM3Armazenado,
      formularioB2bArmazenagem.valorPacoteEnvioFull,
      formularioB2bArmazenagem.prazoDescontoMeses,
      formularioB2bArmazenagem.slaAtrasoPercentual,
      formularioB2bArmazenagem.slaFalhaPercentual,
      formularioB2bArmazenagem.valorUnidadeRetrabalhada,
    ];
    if (!formularioB2bArmazenagem.emitenteId || numeros.some((valor) => valor === "" || !Number.isFinite(Number(valor)) || Number(valor) < 0)) {
      toast.error("Preencha o emitente e todos os valores B2B maiores ou iguais a zero.");
      return;
    }
    salvarConfiguracaoB2bArmazenagem.mutate({
      ...(b2bEditingId ? { id: b2bEditingId } : {}),
      emitenteId: Number(formularioB2bArmazenagem.emitenteId),
      valorPorPedido: Number(formularioB2bArmazenagem.valorPorPedido),
      valorPorSku: Number(formularioB2bArmazenagem.valorPorSku),
      valorPorQuantidade: Number(formularioB2bArmazenagem.valorPorQuantidade),
      quantidadeBonificadaPorSku: Number(formularioB2bArmazenagem.quantidadeBonificadaPorSku),
      valorM3Armazenado: Number(formularioB2bArmazenagem.valorM3Armazenado),
      valorPacoteEnvioFull: Number(formularioB2bArmazenagem.valorPacoteEnvioFull),
      prazoDescontoMeses: Number(formularioB2bArmazenagem.prazoDescontoMeses),
      slaAtrasoPercentual: Number(formularioB2bArmazenagem.slaAtrasoPercentual),
      slaFalhaPercentual: Number(formularioB2bArmazenagem.slaFalhaPercentual),
      valorUnidadeRetrabalhada: Number(formularioB2bArmazenagem.valorUnidadeRetrabalhada),
    });
  }

  function limparFormulario() {
    setEditingId(null);
    setFormulario(formularioVazio);
    setFaixasBonificadas([]);
  }

  function atualizarFormulario(campo: keyof ConfiguracaoForm, valor: string) {
    setFormulario((atual) => ({ ...atual, [campo]: valor }));
  }

  function adicionarFaixa() {
    setFaixasBonificadas((atual) => [...atual, { quantidadePacotes: "", metragemBonificadaM2: "" }]);
  }

  function atualizarFaixa(indice: number, campo: keyof FaixaBonificadaForm, valor: string) {
    setFaixasBonificadas((atual) => atual.map((faixa, index) => index === indice ? { ...faixa, [campo]: valor } : faixa));
  }

  function removerFaixa(indice: number) {
    setFaixasBonificadas((atual) => atual.filter((_, index) => index !== indice));
  }

  function editarConfiguracao(configuracao: any) {
    setAbaAtiva("configuracoes");
    setEditingId(configuracao.id);
    setFormulario({
      emitenteId: String(configuracao.emitenteId),
      dataInicio: String(configuracao.dataInicioFormatado || configuracao.dataInicio).slice(0, 10),
      periodicidadeReajuste: configuracao.periodicidadeReajuste,
      valorM2Armazenado: String(configuracao.valorM2Armazenado ?? ""),
      valorPacoteExpedido: String(configuracao.valorPacoteExpedido ?? ""),
      valorPacoteEnvioFull: String(configuracao.valorPacoteEnvioFull ?? ""),
      prazoDescontoMeses: String(configuracao.prazoDescontoMeses ?? ""),
      slaAtrasoPercentual: String(configuracao.slaAtrasoPercentual ?? ""),
      slaFalhaPercentual: String(configuracao.slaFalhaPercentual ?? ""),
      valorUnidadeRetrabalhada: String(configuracao.valorUnidadeRetrabalhada ?? ""),
    });
    setFaixasBonificadas((configuracao.faixasBonificadas || []).map((faixa: any) => ({
      quantidadePacotes: String(faixa.quantidadePacotes),
      metragemBonificadaM2: String(faixa.metragemBonificadaM2),
    })));
  }

  function abrirDuplicacao(configuracao: any) {
    setConfiguracaoParaDuplicar(configuracao);
    setEmitenteDestinoId("");
  }

  function confirmarDuplicacao() {
    if (!configuracaoParaDuplicar || !emitenteDestinoId) {
      toast.error("Selecione o emitente de destino.");
      return;
    }

    duplicarConfiguracao.mutate({
      configuracaoOrigemId: configuracaoParaDuplicar.id,
      emitenteDestinoId: Number(emitenteDestinoId),
    });
  }

  async function importarSaldosArmazenagem(evento: React.ChangeEvent<HTMLInputElement>) {
    const arquivo = evento.target.files?.[0];
    if (!arquivo) return;

    try {
      const XLSX = await import("xlsx");
      const arquivoBase64 = await new Promise<string>((resolve, reject) => {
        const leitor = new FileReader();
        leitor.onload = () => resolve(String(leitor.result || "").split(",")[1] || "");
        leitor.onerror = () => reject(new Error("Não foi possível ler o arquivo de saldo."));
        leitor.readAsDataURL(arquivo);
      });
      const workbook = XLSX.read(arquivoBase64, { type: "base64" });
      const primeiraAba = workbook.SheetNames[0];
      const linhas = XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[primeiraAba], { defval: "" });
      const colunas = Object.keys(linhas[0] || {});
      const colunaPorNome = (nome: string) => colunas.find((coluna) => coluna.trim().toLowerCase() === nome.toLowerCase());
      const colunaSku = colunaPorNome("SKU");
      const colunaQuantidade = colunaPorNome("Quantidade");
      if (!colunaSku || !colunaQuantidade) {
        throw new Error("A primeira aba deve conter as colunas SKU e Quantidade.");
      }

      const saldos = linhas.map((linha, indice) => {
        const numeroLinha = indice + 2;
        const sku = String(linha[colunaSku] || "").trim();
        const quantidade = typeof linha[colunaQuantidade] === "number"
          ? Number(linha[colunaQuantidade])
          : Number(String(linha[colunaQuantidade] || "").trim().replace(",", "."));
        if (!sku || !Number.isFinite(quantidade) || quantidade < 0) {
          throw new Error(`Linha ${numeroLinha}: informe SKU e Quantidade válida maior ou igual a zero.`);
        }
        return { sku, quantidade };
      });
      if (saldos.length === 0) {
        throw new Error("A planilha não possui linhas de saldo.");
      }

      setSaldosArmazenagem(saldos);
      setNomeArquivoArmazenagem(arquivo.name);
      setArquivoSaldoBase64(arquivoBase64);
      toast.success(`${saldos.length} linha(s) de saldo carregada(s).`);
    } catch (erro: any) {
      setSaldosArmazenagem([]);
      setNomeArquivoArmazenagem("");
      setArquivoSaldoBase64("");
      toast.error(erro.message || "Erro ao ler a planilha de armazenagem.");
    } finally {
      evento.target.value = "";
    }
  }

  function gerarFechamentoB2cArmazenagem() {
    if (!emitenteRelatorioId || !competencia) {
      toast.error("Selecione o emitente e a competência.");
      return;
    }
    if (saldosArmazenagem.length === 0 || !arquivoSaldoBase64 || !nomeArquivoArmazenagem) {
      toast.error("Envie a planilha de saldo com as colunas SKU e Quantidade.");
      return;
    }
    gerarB2cArmazenagem.mutate({
      emitenteId: Number(emitenteRelatorioId),
      competencia,
      saldos: saldosArmazenagem,
      arquivoSaldoBase64,
      nomeArquivoSaldo: nomeArquivoArmazenagem,
    });
  }

  function alternarRelatorioUnificacao(relatorioId: number) {
    const selecionado = relatoriosUnificacaoIds.includes(relatorioId);
    const proximos = selecionado
      ? relatoriosUnificacaoIds.filter((id) => id !== relatorioId)
      : [...relatoriosUnificacaoIds, relatorioId];
    setRelatoriosUnificacaoIds(proximos);
    if (selecionado && relatoriosUnificaveis.find((relatorio: any) => relatorio.id === relatorioId)?.emitenteId === Number(emitentePrincipalUnificacaoId)) {
      setEmitentePrincipalUnificacaoId("");
    }
  }

  function selecionarPrincipalUnificacao(relatorio: any) {
    setEmitentePrincipalUnificacaoId(String(relatorio.emitenteId));
    setRelatoriosUnificacaoIds((atuais) => atuais.includes(relatorio.id) ? atuais : [...atuais, relatorio.id]);
  }

  function gerarFechamentoUnificado() {
    if (!competencia) {
      toast.error("Selecione a competência dos fechamentos a unificar.");
      return;
    }
    if (!emitentePrincipalUnificacaoId) {
      toast.error("Selecione a empresa principal.");
      return;
    }
    if (relatoriosUnificacaoIds.length < 2) {
      toast.error("Selecione ao menos dois fechamentos salvos da competência.");
      return;
    }
    gerarUnificado.mutate({
      emitentePrincipalId: Number(emitentePrincipalUnificacaoId),
      competencia,
      fechamentoRelatorioIds: relatoriosUnificacaoIds,
    });
  }

  function salvar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault();

    const numeros = [
      formulario.valorM2Armazenado,
      formulario.valorPacoteExpedido,
      formulario.valorPacoteEnvioFull,
      formulario.prazoDescontoMeses,
      formulario.slaAtrasoPercentual,
      formulario.slaFalhaPercentual,
      formulario.valorUnidadeRetrabalhada,
    ];

    if (!formulario.emitenteId || !formulario.dataInicio || numeros.some((valor) => valor === "" || !Number.isFinite(Number(valor)))) {
      toast.error("Preencha todos os campos da configuração.");
      return;
    }

    if (faixasBonificadas.some((faixa) => faixa.quantidadePacotes === "" || faixa.metragemBonificadaM2 === "" || Number(faixa.quantidadePacotes) <= 0 || Number(faixa.metragemBonificadaM2) <= 0)) {
      toast.error("Preencha quantidades positivas em todas as faixas bonificadas.");
      return;
    }

    salvarConfiguracao.mutate({
      ...(editingId ? { id: editingId } : {}),
      emitenteId: Number(formulario.emitenteId),
      dataInicio: formulario.dataInicio,
      periodicidadeReajuste: formulario.periodicidadeReajuste,
      valorM2Armazenado: Number(formulario.valorM2Armazenado),
      valorPacoteExpedido: Number(formulario.valorPacoteExpedido),
      valorPacoteEnvioFull: Number(formulario.valorPacoteEnvioFull),
      prazoDescontoMeses: Number(formulario.prazoDescontoMeses),
      slaAtrasoPercentual: Number(formulario.slaAtrasoPercentual),
      slaFalhaPercentual: Number(formulario.slaFalhaPercentual),
      valorUnidadeRetrabalhada: Number(formulario.valorUnidadeRetrabalhada),
      faixasBonificadas: faixasBonificadas.map((faixa) => ({
        quantidadePacotes: Number(faixa.quantidadePacotes),
        metragemBonificadaM2: Number(faixa.metragemBonificadaM2),
      })),
    });
  }

  if (user && !isAdmin) {
    return <div className="p-6 text-sm text-muted-foreground">Acesso restrito a administradores.</div>;
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">FECHAMENTOS - BOX</h1>
        <p className="text-sm text-muted-foreground">Parâmetros de fechamento do operador logístico para os emitentes atendidos.</p>
      </div>

      <div className="flex gap-2 border-b">
        <Button variant={abaAtiva === "fechamento" ? "default" : "ghost"} onClick={() => setAbaAtiva("fechamento")}>Fechamentos</Button>
        <Button variant={abaAtiva === "controleEmissao" ? "default" : "ghost"} onClick={() => setAbaAtiva("controleEmissao")}>Controle de emissão</Button>
        <Button variant={abaAtiva === "configuracoes" ? "default" : "ghost"} onClick={() => setAbaAtiva("configuracoes")}>Configurações</Button>
      </div>

      {abaAtiva === "fechamento" ? (
        <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Selecionar fechamento</CardTitle>
            <CardDescription>{tipoFechamento === "b2c_armazenagem" ? "Selecione o emitente, a competência e a planilha de saldo para gerar o fechamento B2C + Armazenamento." : "Selecione o emitente e a competência para gerar o fechamento B2B + Armazenagem a partir dos pedidos e do saldo atual do sistema."}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Emitente atendido</Label>
                <Select value={emitenteRelatorioId} onValueChange={setEmitenteRelatorioId}>
                  <SelectTrigger><SelectValue placeholder="Selecione o emitente" /></SelectTrigger>
                  <SelectContent>
                    {emitentesAtendidos.map((emitente: any) => <SelectItem key={emitente.id} value={String(emitente.id)}>{nomeEmitente(emitente)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Competência</Label>
                <Input type="month" value={competencia} onChange={(evento) => {
                  setCompetencia(evento.target.value);
                  setEmitentePrincipalUnificacaoId("");
                  setRelatoriosUnificacaoIds([]);
                }} />
              </div>
              <div className="space-y-2">
                <Label>Tipo de relatório</Label>
                <Select value={tipoFechamento} onValueChange={(valor) => setTipoFechamento(valor as "b2c_armazenagem" | "b2b_armazenagem")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="b2c_armazenagem">B2C + Armazenagem</SelectItem>
                    <SelectItem value="b2b_armazenagem">B2B + Armazenagem</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {tipoFechamento === "b2c_armazenagem" ? <>
              <div className="space-y-3 rounded-md border p-4">
                <div>
                  <h3 className="font-medium">Saldo para armazenagem</h3>
                  <p className="text-sm text-muted-foreground">Envie uma planilha cuja primeira aba tenha as colunas <strong>SKU</strong> e <strong>Quantidade</strong>.</p>
                </div>
                <Input type="file" accept=".xlsx,.xls,.csv" onChange={importarSaldosArmazenagem} />
                <p className="text-sm text-muted-foreground">{nomeArquivoArmazenagem ? `${nomeArquivoArmazenagem}: ${saldosArmazenagem.length} linha(s) carregada(s).` : "Nenhuma planilha de saldo carregada."}</p>
              </div>
              <Button onClick={gerarFechamentoB2cArmazenagem} disabled={gerarB2cArmazenagem.isPending}>
                {gerarB2cArmazenagem.isPending ? <Upload className="mr-2 h-4 w-4" /> : <Download className="mr-2 h-4 w-4" />}
                {gerarB2cArmazenagem.isPending ? "Gerando e salvando..." : "Gerar e salvar nova versão"}
              </Button>
            </> : <>
              <div className="space-y-3 rounded-md border p-4">
                <div>
                  <h3 className="font-medium">Dados para fechamento B2B</h3>
                  <p className="text-sm text-muted-foreground">O B2B usa todos os pedidos do emitente na competência pela data de criação, exceto cancelados, e soma Resolve por data de custo.</p>
                </div>
                <p className="text-sm text-muted-foreground">O saldo para armazenagem é lido diretamente do sistema no momento da geração.</p>
              </div>
              <Button onClick={gerarFechamentoB2bArmazenagem} disabled={gerarB2bArmazenagem.isPending}>
                {gerarB2bArmazenagem.isPending ? <Upload className="mr-2 h-4 w-4" /> : <Download className="mr-2 h-4 w-4" />}
                {gerarB2bArmazenagem.isPending ? "Gerando e salvando..." : "Gerar e salvar nova versão B2B"}
              </Button>
            </>}
          </CardContent>
        </Card>
        {tipoFechamento === "b2b_armazenagem" && <Card>
          <CardHeader>
            <CardTitle>Versões salvas</CardTitle>
            <CardDescription>Histórico de fechamentos B2B + Armazenagem. Cada versão preserva o relatório gerado com o saldo atual do sistema no momento da emissão.</CardDescription>
          </CardHeader>
          <CardContent>
            {carregandoRelatoriosB2bArmazenagem ? <p className="text-sm text-muted-foreground">Carregando versões B2B...</p> : relatoriosB2bArmazenagemFiltrados.length === 0 ? <p className="text-sm text-muted-foreground">Nenhuma versão B2B salva para o filtro atual.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left"><th className="p-2">Emitente</th><th className="p-2">Competência</th><th className="p-2">Versão</th><th className="p-2">Pedidos</th><th className="p-2">Total geral</th><th className="p-2">Criado em</th><th className="p-2">Ação</th></tr></thead>
                  <tbody>
                    {relatoriosB2bArmazenagemFiltrados.map((relatorio: any) => <tr key={relatorio.id} className="border-b align-top">
                      <td className="p-2">{relatorio.emitenteNome}</td>
                      <td className="p-2 whitespace-nowrap">{String(relatorio.competencia).split("-").reverse().join("/")}</td>
                      <td className="p-2">V{relatorio.versao}</td>
                      <td className="p-2">{relatorio.totalPedidos}</td>
                      <td className="p-2 whitespace-nowrap">R$ {valorTela(relatorio.totalGeral)}</td>
                      <td className="p-2 whitespace-nowrap">{relatorio.criadoEm ? new Date(relatorio.criadoEm).toLocaleString("pt-BR") : "-"}</td>
                      <td className="p-2"><Button size="sm" variant="outline" onClick={() => baixarArquivoB2bArmazenagemRelatorio.mutate({ id: relatorio.id })} disabled={baixarArquivoB2bArmazenagemRelatorio.isPending}><Download className="mr-2 h-4 w-4" />Relatório</Button></td>
                    </tr>)}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>}
        {tipoFechamento === "b2c_armazenagem" && <>
        <Card>
          <CardHeader>
            <CardTitle>Unificar fechamentos salvos</CardTitle>
            <CardDescription>Na competência selecionada, escolha a empresa principal e os fechamentos já salvos. O cálculo usa a configuração vigente da principal, soma pedidos, Resolve e armazenagem, e mantém o estoque separado por empresa + SKU.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!competencia ? <p className="text-sm text-muted-foreground">Selecione a competência acima para listar os últimos fechamentos por empresa.</p> : relatoriosUnificaveis.length < 2 ? <p className="text-sm text-muted-foreground">São necessários ao menos dois fechamentos salvos nessa competência.</p> : (
              <>
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-left"><th className="p-2">Incluir</th><th className="p-2">Principal</th><th className="p-2">Empresa</th><th className="p-2">Versão</th><th className="p-2">Total geral</th></tr></thead>
                    <tbody>
                      {relatoriosUnificaveis.map((relatorio: any) => (
                        <tr key={relatorio.id} className="border-b">
                          <td className="p-2"><input type="checkbox" checked={relatoriosUnificacaoIds.includes(relatorio.id)} onChange={() => alternarRelatorioUnificacao(relatorio.id)} /></td>
                          <td className="p-2"><input type="radio" name="empresa-principal-unificacao" checked={Number(emitentePrincipalUnificacaoId) === Number(relatorio.emitenteId)} onChange={() => selecionarPrincipalUnificacao(relatorio)} /></td>
                          <td className="p-2">{relatorio.emitenteNome}</td>
                          <td className="p-2">V{relatorio.versao}</td>
                          <td className="p-2 whitespace-nowrap">R$ {valorTela(relatorio.totalGeral)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Button onClick={gerarFechamentoUnificado} disabled={gerarUnificado.isPending}>
                  {gerarUnificado.isPending ? <Upload className="mr-2 h-4 w-4" /> : <Download className="mr-2 h-4 w-4" />}
                  {gerarUnificado.isPending ? "Unificando e salvando..." : "Unificar e baixar novo Excel"}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Fechamentos unificados</CardTitle>
            <CardDescription>O Excel unificado substitui o vínculo ativo dos fechamentos selecionados, sem alterar seus arquivos individuais.</CardDescription>
          </CardHeader>
          <CardContent>
            {carregandoUnificacoes ? <p className="text-sm text-muted-foreground">Carregando fechamentos unificados...</p> : unificacoes.filter((unificacao: any) => !competencia || unificacao.competencia === competencia).length === 0 ? <p className="text-sm text-muted-foreground">Nenhum fechamento unificado para o filtro atual.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left"><th className="p-2">Principal</th><th className="p-2">Competência</th><th className="p-2">Empresas unificadas</th><th className="p-2">Total geral</th><th className="p-2">Ações</th></tr></thead>
                  <tbody>
                    {unificacoes.filter((unificacao: any) => !competencia || unificacao.competencia === competencia).map((unificacao: any) => (
                      <tr key={unificacao.id} className="border-b align-top">
                        <td className="p-2">{unificacao.emitentePrincipalNome}</td>
                        <td className="p-2 whitespace-nowrap">{String(unificacao.competencia).split("-").reverse().join("/")}</td>
                        <td className="p-2">{unificacao.relatoriosFonte.map((fonte: any) => `${fonte.emitenteNome} (V${fonte.versao})`).join(" + ")}</td>
                        <td className="p-2 whitespace-nowrap">R$ {valorTela(unificacao.totalGeral)}</td>
                        <td className="p-2"><div className="flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" onClick={() => baixarArquivoUnificado.mutate({ id: unificacao.id, tipoArquivo: "relatorio" })} disabled={baixarArquivoUnificado.isPending}><Download className="mr-2 h-4 w-4" />Relatório</Button>
                          <Button size="sm" variant="outline" onClick={() => baixarArquivoUnificado.mutate({ id: unificacao.id, tipoArquivo: "saldo_padronizado" })} disabled={baixarArquivoUnificado.isPending}>Saldo padrão</Button>
                          <Button size="sm" variant="outline" onClick={() => baixarArquivoUnificado.mutate({ id: unificacao.id, tipoArquivo: "saldo_fontes" })} disabled={baixarArquivoUnificado.isPending}>Saldo por empresa</Button>
                        </div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Versões salvas</CardTitle>
            <CardDescription>Histórico de fechamentos B2C + Armazenagem. Cada versão preserva o relatório e as duas planilhas de saldo usadas na geração.</CardDescription>
          </CardHeader>
          <CardContent>
            {carregandoRelatorios ? <p className="text-sm text-muted-foreground">Carregando versões salvas...</p> : relatoriosFiltrados.length === 0 ? <p className="text-sm text-muted-foreground">Nenhuma versão salva para o filtro atual.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left"><th className="p-2">Emitente</th><th className="p-2">Competência</th><th className="p-2">Versão</th><th className="p-2">Total geral</th><th className="p-2">Criado em</th><th className="p-2">Ações</th></tr></thead>
                  <tbody>
                    {relatoriosFiltrados.map((relatorio: any) => (
                      <tr key={relatorio.id} className="border-b align-top">
                        <td className="p-2">
                          <div>{relatorio.emitenteNome}</div>
                          {relatorio.unificacaoId && Number(relatorio.emitenteId) !== Number(relatorio.unificacaoEmitentePrincipalId) && <p className="mt-1 text-xs text-muted-foreground">Unificado para {relatorio.unificacaoEmitentePrincipalNome} · {String(relatorio.unificacaoCompetencia).split("-").reverse().join("/")} · por {relatorio.unificacaoCriadoPorNome}</p>}
                        </td>
                        <td className="p-2 whitespace-nowrap">{String(relatorio.competencia).split("-").reverse().join("/")}</td>
                        <td className="p-2">V{relatorio.versao}</td>
                        <td className="p-2 whitespace-nowrap">R$ {valorTela(relatorio.totalGeral)}</td>
                        <td className="p-2 whitespace-nowrap">{relatorio.criadoEm ? new Date(relatorio.criadoEm).toLocaleString("pt-BR") : "-"}</td>
                        <td className="p-2">
                          <div className="flex flex-wrap gap-2">
                            <Button size="sm" variant="outline" onClick={() => baixarArquivoRelatorio.mutate({ id: relatorio.id, tipoArquivo: "relatorio" })} disabled={baixarArquivoRelatorio.isPending || Boolean(relatorio.unificacaoId)}><Download className="mr-2 h-4 w-4" />Relatório</Button>
                            <Button size="sm" variant="outline" onClick={() => baixarArquivoRelatorio.mutate({ id: relatorio.id, tipoArquivo: "saldo_original" })} disabled={baixarArquivoRelatorio.isPending || Boolean(relatorio.unificacaoId)}>Saldo original</Button>
                            <Button size="sm" variant="outline" onClick={() => baixarArquivoRelatorio.mutate({ id: relatorio.id, tipoArquivo: "saldo_padronizado" })} disabled={baixarArquivoRelatorio.isPending || Boolean(relatorio.unificacaoId)}>Saldo padrão</Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        </>}
        </div>
      ) : abaAtiva === "controleEmissao" ? (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                <div>
                  <CardTitle>Controle anual de emissão</CardTitle>
                  <CardDescription>Verde: existe fechamento B2C + Armazenagem ou B2B + Armazenagem para o emitente e a competência. Vermelho: falta emitir. Janeiro a julho de 2026 não se aplicam.</CardDescription>
                </div>
                <div className="w-full space-y-2 md:w-40">
                  <Label>Ano</Label>
                  <Select value={anoControleEmissao} onValueChange={setAnoControleEmissao}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {anosControleEmissao.map((ano) => <SelectItem key={ano} value={String(ano)}>{ano}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {carregandoRelatorios || carregandoRelatoriosB2bArmazenagem ? <p className="text-sm text-muted-foreground">Carregando controle de emissão...</p> : (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-left"><th className="p-2 whitespace-nowrap">Emitente</th>{mesesControleEmissao.map((mesNome) => <th key={mesNome} className="p-2 text-center whitespace-nowrap">{mesNome}</th>)}</tr></thead>
                    <tbody>
                      {emitentesAtendidos.map((emitente: any) => (
                        <tr key={emitente.id} className="border-b">
                          <td className="p-2 whitespace-nowrap font-medium">{nomeEmitente(emitente)}</td>
                          {mesesControleEmissao.map((mesNome, indice) => {
                            const mes = indice + 1;
                            const competenciaControle = `${anoControleEmissao}-${String(mes).padStart(2, "0")}`;
                            const naoAplicavel = competenciaNaoAplicavel(mes);
                            const emitido = competenciasEmitidas.has(`${Number(emitente.id)}-${competenciaControle}`);
                            return <td key={mesNome} className="p-2 text-center">
                              {naoAplicavel ? <span className="text-muted-foreground" title="Não aplicável">—</span> : <span className={`inline-block h-3 w-3 rounded-full ${emitido ? "bg-emerald-500" : "bg-red-500"}`} title={emitido ? "Emitido" : "Falta emitir"} aria-label={emitido ? "Emitido" : "Falta emitir"} />}
                            </td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Falta emitir</CardTitle>
              <CardDescription>Competências marcadas em vermelho no controle anual, somente até o mês anterior ao atual.</CardDescription>
            </CardHeader>
            <CardContent>
              {carregandoRelatorios || carregandoRelatoriosB2bArmazenagem ? <p className="text-sm text-muted-foreground">Carregando pendências...</p> : faltaEmitir.length === 0 ? <p className="text-sm text-muted-foreground">Nenhuma competência pendente para o ano selecionado.</p> : (
                <div className="max-h-[420px] overflow-y-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-background"><tr className="border-b text-left"><th className="p-2">Emitente</th><th className="p-2">Competência</th></tr></thead>
                    <tbody>
                      {faltaEmitir.map((pendencia) => <tr key={`${pendencia.emitenteNome}-${pendencia.competencia}`} className="border-b"><td className="p-2">{pendencia.emitenteNome}</td><td className="p-2 whitespace-nowrap">{pendencia.mesNome}/{anoControleEmissao}</td></tr>)}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <CardTitle>{editingId ? "Editar configuração" : "Nova configuração"}</CardTitle>
                  <CardDescription>Registre parâmetros por emitente e data de início. Nenhum cálculo é executado nesta tela.</CardDescription>
                </div>
                {editingId && <Button variant="outline" onClick={limparFormulario}><X className="mr-2 h-4 w-4" />Cancelar edição</Button>}
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={salvar} className="space-y-6">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label>Emitente atendido</Label>
                    <Select value={formulario.emitenteId} onValueChange={(valor) => atualizarFormulario("emitenteId", valor)}>
                      <SelectTrigger><SelectValue placeholder="Selecione o emitente" /></SelectTrigger>
                      <SelectContent>
                        {emitentesAtendidos.map((emitente: any) => <SelectItem key={emitente.id} value={String(emitente.id)}>{nomeEmitente(emitente)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Data de início</Label>
                    <Input type="date" value={formulario.dataInicio} onChange={(evento) => atualizarFormulario("dataInicio", evento.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Tempo de reajuste</Label>
                    <Select value={formulario.periodicidadeReajuste} onValueChange={(valor) => atualizarFormulario("periodicidadeReajuste", valor as ConfiguracaoForm["periodicidadeReajuste"])}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {periodicidades.map((periodicidade) => <SelectItem key={periodicidade.value} value={periodicidade.value}>{periodicidade.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <CampoNumerico label="Valor por m³ armazenado (R$)" value={formulario.valorM2Armazenado} onChange={(valor) => atualizarFormulario("valorM2Armazenado", valor)} step="0.0001" />
                  <CampoNumerico label="Valor por pacote expedido (R$)" value={formulario.valorPacoteExpedido} onChange={(valor) => atualizarFormulario("valorPacoteExpedido", valor)} step="0.0001" />
                  <CampoNumerico label="Valor por pacote de envio Full (R$)" value={formulario.valorPacoteEnvioFull} onChange={(valor) => atualizarFormulario("valorPacoteEnvioFull", valor)} step="0.0001" />
                  <CampoNumerico label="Prazo de desconto (meses)" value={formulario.prazoDescontoMeses} onChange={(valor) => atualizarFormulario("prazoDescontoMeses", valor)} step="1" />
                  <CampoNumerico label="SLA de atraso (%)" value={formulario.slaAtrasoPercentual} onChange={(valor) => atualizarFormulario("slaAtrasoPercentual", valor)} step="0.0001" />
                  <CampoNumerico label="SLA de falha (%)" value={formulario.slaFalhaPercentual} onChange={(valor) => atualizarFormulario("slaFalhaPercentual", valor)} step="0.0001" />
                  <CampoNumerico label="Valor por unidade retrabalhada (R$)" value={formulario.valorUnidadeRetrabalhada} onChange={(valor) => atualizarFormulario("valorUnidadeRetrabalhada", valor)} step="0.0001" />
                </div>

                <div className="space-y-3 rounded-md border p-4">
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h3 className="font-medium">Metragem bonificada por pacotes</h3>
                      <p className="text-sm text-muted-foreground">Adicione cada faixa de quantidade de pacotes e os m³ bonificados correspondentes.</p>
                    </div>
                    <Button type="button" variant="outline" onClick={adicionarFaixa}><Plus className="mr-2 h-4 w-4" />Adicionar faixa</Button>
                  </div>
                  {faixasBonificadas.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nenhuma faixa bonificada cadastrada.</p>
                  ) : faixasBonificadas.map((faixa, indice) => (
                    <div key={indice} className="grid gap-3 md:grid-cols-[1fr_1fr_auto] md:items-end">
                      <CampoNumerico label="Quantidade de pacotes" value={faixa.quantidadePacotes} onChange={(valor) => atualizarFaixa(indice, "quantidadePacotes", valor)} step="1" />
                      <CampoNumerico label="Metragem bonificada (m³)" value={faixa.metragemBonificadaM2} onChange={(valor) => atualizarFaixa(indice, "metragemBonificadaM2", valor)} step="0.0001" />
                      <Button type="button" variant="outline" onClick={() => removerFaixa(indice)}>Remover</Button>
                    </div>
                  ))}
                </div>

                <Button type="submit" disabled={salvarConfiguracao.isPending}>
                  <Save className="mr-2 h-4 w-4" />{salvarConfiguracao.isPending ? "Salvando..." : "Salvar configuração"}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Configurações registradas</CardTitle>
              <CardDescription>Histórico por emitente e data de início.</CardDescription>
            </CardHeader>
            <CardContent>
              {carregandoConfiguracoes ? <p className="text-sm text-muted-foreground">Carregando configurações...</p> : configuracoes.length === 0 ? <p className="text-sm text-muted-foreground">Nenhuma configuração cadastrada.</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-left"><th className="p-2">Emitente</th><th className="p-2">Início</th><th className="p-2">Reajuste</th><th className="p-2">Armazenagem/m³</th><th className="p-2">Pacote</th><th className="p-2">Full</th><th className="p-2">Faixas</th><th className="p-2">Ação</th></tr></thead>
                    <tbody>
                      {configuracoes.map((configuracao: any) => (
                        <tr key={configuracao.id} className="border-b align-top">
                          <td className="p-2">{configuracao.emitenteNome}</td>
                          <td className="p-2 whitespace-nowrap">{String(configuracao.dataInicioFormatado || configuracao.dataInicio).slice(0, 10).split("-").reverse().join("/")}</td>
                          <td className="p-2 capitalize">{configuracao.periodicidadeReajuste}</td>
                          <td className="p-2 whitespace-nowrap">R$ {valorTela(configuracao.valorM2Armazenado, 4)}</td>
                          <td className="p-2 whitespace-nowrap">R$ {valorTela(configuracao.valorPacoteExpedido, 4)}</td>
                          <td className="p-2 whitespace-nowrap">R$ {valorTela(configuracao.valorPacoteEnvioFull, 4)}</td>
                          <td className="p-2">{(configuracao.faixasBonificadas || []).length}</td>
                          <td className="p-2">
                            <div className="flex flex-wrap gap-2">
                              <Button size="sm" variant="outline" onClick={() => editarConfiguracao(configuracao)}><Pencil className="mr-2 h-4 w-4" />Editar</Button>
                              <Button size="sm" variant="outline" onClick={() => abrirDuplicacao(configuracao)}><Copy className="mr-2 h-4 w-4" />Duplicar</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <CardTitle>{b2bEditingId ? "Editar configuração B2B + Armazenagem" : "Nova configuração B2B + Armazenagem"}</CardTitle>
                  <CardDescription>Parâmetros exclusivos do cálculo B2B. A competência limita os pedidos pela data de criação; o saldo de armazenagem é lido diretamente do sistema no momento de gerar o relatório.</CardDescription>
                </div>
                {b2bEditingId && <Button variant="outline" onClick={limparFormularioB2bArmazenagem}><X className="mr-2 h-4 w-4" />Cancelar edição</Button>}
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={salvarB2bArmazenagem} className="space-y-6">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label>Emitente atendido</Label>
                    <Select value={formularioB2bArmazenagem.emitenteId} onValueChange={(valor) => atualizarFormularioB2bArmazenagem("emitenteId", valor)}>
                      <SelectTrigger><SelectValue placeholder="Selecione o emitente" /></SelectTrigger>
                      <SelectContent>
                        {emitentesAtendidos.map((emitente: any) => <SelectItem key={emitente.id} value={String(emitente.id)}>{nomeEmitente(emitente)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <CampoNumerico label="Valor por pedido (R$)" value={formularioB2bArmazenagem.valorPorPedido} onChange={(valor) => atualizarFormularioB2bArmazenagem("valorPorPedido", valor)} step="0.0001" />
                  <CampoNumerico label="Valor por SKU distinto (R$)" value={formularioB2bArmazenagem.valorPorSku} onChange={(valor) => atualizarFormularioB2bArmazenagem("valorPorSku", valor)} step="0.0001" />
                  <CampoNumerico label="Valor por quantidade (R$)" value={formularioB2bArmazenagem.valorPorQuantidade} onChange={(valor) => atualizarFormularioB2bArmazenagem("valorPorQuantidade", valor)} step="0.0001" />
                  <CampoNumerico label="Quantidade bonificada por SKU" value={formularioB2bArmazenagem.quantidadeBonificadaPorSku} onChange={(valor) => atualizarFormularioB2bArmazenagem("quantidadeBonificadaPorSku", valor)} step="0.0001" />
                  <CampoNumerico label="Valor por m³ armazenado (R$)" value={formularioB2bArmazenagem.valorM3Armazenado} onChange={(valor) => atualizarFormularioB2bArmazenagem("valorM3Armazenado", valor)} step="0.0001" />
                  <CampoNumerico label="Valor por pacote de envio Full (R$)" value={formularioB2bArmazenagem.valorPacoteEnvioFull} onChange={(valor) => atualizarFormularioB2bArmazenagem("valorPacoteEnvioFull", valor)} step="0.0001" />
                  <CampoNumerico label="Prazo de desconto (meses)" value={formularioB2bArmazenagem.prazoDescontoMeses} onChange={(valor) => atualizarFormularioB2bArmazenagem("prazoDescontoMeses", valor)} step="1" />
                  <CampoNumerico label="SLA de atraso (%)" value={formularioB2bArmazenagem.slaAtrasoPercentual} onChange={(valor) => atualizarFormularioB2bArmazenagem("slaAtrasoPercentual", valor)} step="0.0001" />
                  <CampoNumerico label="SLA de falha (%)" value={formularioB2bArmazenagem.slaFalhaPercentual} onChange={(valor) => atualizarFormularioB2bArmazenagem("slaFalhaPercentual", valor)} step="0.0001" />
                  <CampoNumerico label="Valor por unidade retrabalhada (R$)" value={formularioB2bArmazenagem.valorUnidadeRetrabalhada} onChange={(valor) => atualizarFormularioB2bArmazenagem("valorUnidadeRetrabalhada", valor)} step="0.0001" />
                </div>
                <Button type="submit" disabled={salvarConfiguracaoB2bArmazenagem.isPending}>
                  <Save className="mr-2 h-4 w-4" />{salvarConfiguracaoB2bArmazenagem.isPending ? "Salvando..." : "Salvar configuração B2B"}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Configurações B2B registradas</CardTitle>
              <CardDescription>Uma configuração independente por emitente para B2B + Armazenagem.</CardDescription>
            </CardHeader>
            <CardContent>
              {carregandoConfiguracoesB2bArmazenagem ? <p className="text-sm text-muted-foreground">Carregando configurações B2B...</p> : configuracoesB2bArmazenagem.length === 0 ? <p className="text-sm text-muted-foreground">Nenhuma configuração B2B cadastrada.</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-left"><th className="p-2">Emitente</th><th className="p-2">Pedido</th><th className="p-2">SKU</th><th className="p-2">Quantidade</th><th className="p-2">Bonificação/SKU</th><th className="p-2">Armazenagem/m³</th><th className="p-2">Full</th><th className="p-2">Retrabalho</th><th className="p-2">Ação</th></tr></thead>
                    <tbody>
                      {configuracoesB2bArmazenagem.map((configuracao: any) => <tr key={configuracao.id} className="border-b align-top">
                        <td className="p-2">{configuracao.emitenteNome}</td>
                        <td className="p-2 whitespace-nowrap">R$ {valorTela(configuracao.valorPorPedido, 4)}</td>
                        <td className="p-2 whitespace-nowrap">R$ {valorTela(configuracao.valorPorSku, 4)}</td>
                        <td className="p-2 whitespace-nowrap">R$ {valorTela(configuracao.valorPorQuantidade, 4)}</td>
                        <td className="p-2">{valorTela(configuracao.quantidadeBonificadaPorSku, 4)}</td>
                        <td className="p-2 whitespace-nowrap">R$ {valorTela(configuracao.valorM3Armazenado, 4)}</td>
                        <td className="p-2 whitespace-nowrap">R$ {valorTela(configuracao.valorPacoteEnvioFull, 4)}</td>
                        <td className="p-2 whitespace-nowrap">R$ {valorTela(configuracao.valorUnidadeRetrabalhada, 4)}</td>
                        <td className="p-2"><Button size="sm" variant="outline" onClick={() => editarConfiguracaoB2bArmazenagem(configuracao)}><Pencil className="mr-2 h-4 w-4" />Editar</Button></td>
                      </tr>)}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog open={!!configuracaoParaDuplicar} onOpenChange={(aberto) => {
        if (!aberto) {
          setConfiguracaoParaDuplicar(null);
          setEmitenteDestinoId("");
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicar configuração</DialogTitle>
            <DialogDescription>
              A configuração e todas as faixas bonificadas serão copiadas para o emitente de destino com a mesma data de início.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Emitente de destino</Label>
            <Select value={emitenteDestinoId} onValueChange={setEmitenteDestinoId}>
              <SelectTrigger><SelectValue placeholder="Selecione o emitente" /></SelectTrigger>
              <SelectContent>
                {emitentesAtendidos
                  .filter((emitente: any) => emitente.id !== configuracaoParaDuplicar?.emitenteId)
                  .map((emitente: any) => <SelectItem key={emitente.id} value={String(emitente.id)}>{nomeEmitente(emitente)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfiguracaoParaDuplicar(null)} disabled={duplicarConfiguracao.isPending}>Cancelar</Button>
            <Button onClick={confirmarDuplicacao} disabled={duplicarConfiguracao.isPending}>{duplicarConfiguracao.isPending ? "Duplicando..." : "Duplicar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CampoNumerico({ label, value, onChange, step }: { label: string; value: string; onChange: (valor: string) => void; step: string }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input type="number" min="0" step={step} value={value} onChange={(evento) => onChange(evento.target.value)} />
    </div>
  );
}
